import type { CountryCode } from 'libphonenumber-js/max';
import {
  getCountries,
  getCountryCallingCode,
  isValidPhoneNumber as libIsValidPhoneNumber,
  parsePhoneNumber,
} from 'libphonenumber-js/max';

/**
 * Ported from `voicebot-portal/src/shared/validations/phone.ts` so both front ends judge a number the same way.
 * Keep the two in sync; the portal is the original.
 */

/**
 * Uses the `/max` `isValidPhoneNumber`, which is stricter than `parsePhoneNumber().isValid()` - the latter
 * strips letters and reads a semicolon as an RFC 3966 extension. Surrounding whitespace is trimmed rather than
 * rejected; inner junk ("089 1234567 abc") still fails.
 */
export function isValidPhoneNumber(value: string, countryCode: string): boolean {
  if (value.includes(';')) {
    return false;
  }
  try {
    return libIsValidPhoneNumber(value.trim(), countryCode as CountryCode);
  } catch {
    return false;
  }
}

/**
 * Normalise to E.164 ("089 1234567" + "DE" -> "+49891234567"), or `undefined` when the input is not valid.
 * Store what this returns, not what the user typed: validating without normalising is what let the portal send
 * "089 1234567" on with its space intact, and E.164 also satisfies the old CMT's no-whitespace restriction.
 */
export function toE164(value: string, countryCode: string): string | undefined {
  const trimmed = value.trim();
  if (!isValidPhoneNumber(trimmed, countryCode)) {
    return undefined;
  }
  try {
    return parsePhoneNumber(trimmed, countryCode as CountryCode).format('E.164');
  } catch {
    return undefined;
  }
}

/**
 * ISO country for a numeric calling code, so the default region comes from the phone service being edited
 * instead of a hardcoded "DE" - "044 797 88 00" is valid in Germany and Switzerland. A code shared by several
 * countries (+1) resolves to the first match, which is harmless: E.164 is identical for all of them.
 */
export function countryFromCallingCode(callingCode: string | null | undefined): string | undefined {
  if (!callingCode) {
    return undefined;
  }
  const normalised = callingCode.replace(/^\+/, '');
  return getCountries().find(country => getCountryCallingCode(country) === normalised);
}
